import { Injectable, NgZone } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { SKIP_GLOBAL_LOADING } from './global-loading.context';
import {
  AppUpdatePlatform,
  AppUpdateSetting,
  dismissedUpdateStorageKey,
} from './app-version';

export interface ApkBuildFile {
  name: string;
  label?: string;
  url: string;
  size: number;
  modified_label: string | null;
  kind: 'apk' | 'aab';
  debug: boolean;
  version: number | null;
}

export interface ApkBuildFolder {
  id: 'apk' | 'release' | string;
  path: string;
  url: string;
  error: string | null;
  files: ApkBuildFile[];
}

@Injectable({ providedIn: 'root' })
export class AppUpdateService {
  forceUpdate = false;
  optionalUpdate = false;
  optionalUpdateMinimized = false;
  prompt: AppUpdateSetting | null = null;
  currentVersion = '';
  checking = false;

  private readonly apiUrl = environment.apiUrl;
  private lastCheckedKey = '';

  constructor(
    private http: HttpClient,
    private ngZone: NgZone,
  ) {}

  getAdminApkBuilds() {
    return this.http.get<{
      base_url: string;
      folders: ApkBuildFolder[];
    }>(`${this.apiUrl}/admin/apk-builds`, {
      context: new HttpContext().set(SKIP_GLOBAL_LOADING, true),
    });
  }

  getAdminSettings() {
    return this.http.get<AppUpdateSetting[]>(`${this.apiUrl}/admin/app-update`, {
      context: new HttpContext().set(SKIP_GLOBAL_LOADING, true),
    });
  }

  saveAdminSetting(setting: Partial<AppUpdateSetting> & { platform: AppUpdatePlatform }) {
    return this.http.put<AppUpdateSetting>(
      `${this.apiUrl}/admin/app-update`,
      setting,
    );
  }

  async check(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      this.clearPrompt();
      return;
    }

    const platform = Capacitor.getPlatform();
    if (platform !== 'ios' && platform !== 'android') {
      this.clearPrompt();
      return;
    }

    let currentVersion = '';
    try {
      const info = await App.getInfo();
      currentVersion = info?.version || '';
    } catch (err) {
      console.warn('[AppUpdate] failed to read native version', err);
      return;
    }

    if (!currentVersion) {
      return;
    }

    this.currentVersion = currentVersion;
    const checkKey = `${platform}:${currentVersion}`;
    if (this.checking && this.lastCheckedKey === checkKey && this.forceUpdate) {
      return;
    }

    this.checking = true;
    this.lastCheckedKey = checkKey;

    try {
      const result = await firstValueFrom(
        this.http.get<AppUpdateSetting>(`${this.apiUrl}/app-update`, {
          params: {
            platform,
            current_version: currentVersion,
          },
          context: new HttpContext().set(SKIP_GLOBAL_LOADING, true),
        }),
      );

      this.ngZone.run(() => this.applyResult(result, platform));
    } catch (err) {
      console.warn('[AppUpdate] check failed', err);
    } finally {
      this.checking = false;
    }
  }

  dismissOptional(): void {
    if (!this.prompt || this.forceUpdate) {
      return;
    }

    localStorage.setItem(
      dismissedUpdateStorageKey(this.prompt.platform, this.prompt.latest_version),
      '1',
    );
    this.optionalUpdate = false;
    this.optionalUpdateMinimized = true;
  }

  async openStore(): Promise<void> {
    const url =
      this.prompt?.store_url ||
      (this.prompt?.platform === 'android'
        ? 'https://play.google.com/store/apps/details?id=com.bidarbak'
        : 'https://apps.apple.com/search?term=Bidarbak');

    window.open(url, '_system');
  }

  private applyResult(
    result: AppUpdateSetting,
    platform: AppUpdatePlatform,
  ): void {
    if (result.force_update) {
      this.forceUpdate = true;
      this.optionalUpdate = false;
      this.optionalUpdateMinimized = false;
      this.prompt = result;
      return;
    }

    this.forceUpdate = false;

    if (result.update_available && result.update_type === 'optional') {
      this.prompt = result;
      const minimized = !!localStorage.getItem(
        dismissedUpdateStorageKey(platform, result.latest_version),
      );
      this.optionalUpdate = !minimized;
      this.optionalUpdateMinimized = minimized;
      return;
    }

    this.optionalUpdate = false;
    this.optionalUpdateMinimized = false;
    this.prompt = null;
  }

  private clearPrompt(): void {
    this.forceUpdate = false;
    this.optionalUpdate = false;
    this.optionalUpdateMinimized = false;
    this.prompt = null;
  }
}
